import { useCallback, useEffect, useMemo, useState } from "react";

/** The stored ids. Without storage, or with a value that is not a list of strings, nothing is open. */
function read(key: string): string[] {
  try {
    const value: unknown = JSON.parse(window.localStorage.getItem(key) ?? "[]");
    return Array.isArray(value) && value.every((v) => typeof v === "string") ? value : [];
  } catch {
    return [];
  }
}

function write(key: string, ids: string[]) {
  try {
    window.localStorage.setItem(key, JSON.stringify(ids));
  } catch {
    // No storage (a private window): the set lives for this visit only.
  }
}

/**
 * The Handbook topics that are open in the sidebar (handbook start spec §4.1). Arriving on a topic's page or
 * on one of its documents (`currentId` becomes that topic) opens it; the arrow's `toggle` can close it again,
 * and it then stays closed until the next arrival. Ids of topics that no longer exist stay in the set and
 * draw nothing. `key` names the stored set: the Templates tree keeps the groups it has folded the same way.
 */
export function useOpenTopics(
  currentId: string | undefined,
  key = "kd.handbook.open",
): {
  open: ReadonlySet<string>;
  toggle: (id: string) => void;
} {
  const [ids, setIds] = useState<string[]>(() => {
    const stored = read(key);
    return currentId && !stored.includes(currentId) ? [...stored, currentId] : stored;
  });
  // An arrival is opened while rendering, not in an effect, so the row never draws closed for a frame.
  const [seen, setSeen] = useState(currentId);
  let shown = ids;
  if (currentId !== seen) {
    setSeen(currentId);
    if (currentId && !ids.includes(currentId)) {
      shown = [...ids, currentId];
      setIds(shown);
    }
  }
  useEffect(() => write(key, ids), [key, ids]);
  const toggle = useCallback(
    (id: string) => setIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])),
    [],
  );
  const open = useMemo(() => new Set(shown), [shown]);
  return { open, toggle };
}
